import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAdmin } from "./admin-context/AdminContext";
import { doAdminLogin } from "../firebase/auth";
import { ClipLoader } from "react-spinners";
import { auth, db } from "../firebase/firebaseConfig";
import { collection, query, where, getDocs } from "firebase/firestore";

// Logout function
export const doAdminLogout = async () => {
  try {
    await auth.signOut();
  } catch (error) {
    console.error("Logout error:", error);
  }
};

const AdminLoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [showOtpPopup, setShowOtpPopup] = useState(false);
  const [showPhonePopup, setShowPhonePopup] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [storedPhoneNumber, setStoredPhoneNumber] = useState(null);

  const timeoutRef = useRef();
  const { isAdmin, setIsAdmin } = useAdmin();
  const navigate = useNavigate();

  useEffect(() => {
    const isAdmin = localStorage.getItem("isAdmin");
    if (isAdmin === "true") {
      navigate("/admin-dashboard");
    }
  }, [navigate]);

  // Countdown effect
  useEffect(() => {
    if (resendDisabled && countdown > 0) {
      timeoutRef.current = setTimeout(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else if (countdown === 0) {
      setResendDisabled(false);
    }
    return () => clearTimeout(timeoutRef.current);
  }, [resendDisabled, countdown]);

  // Cleanup effects
  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  // Check if admin has phone number in Firestore
  const checkAdminPhoneNumber = async (email) => {
    try {
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("email", "==", email));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        const userData = userDoc.data();
        return userData.phoneNumber || null;
      }
      return null;
    } catch (err) {
      console.error("Error checking user phone number:", err);
      return null;
    }
  };

  // Handle login
  const handleLogin = useCallback(
    async (e) => {
      e.preventDefault();
      setError("");

      if (!email || !password) {
        setError("Please enter email and password.");
        return;
      }

      setLoading(true);
      try {
        const userCredential = await doAdminLogin(email, password);
        const user = userCredential.user;

        const phone = await checkAdminPhoneNumber(email);
        setStoredPhoneNumber(phone);

        if (!phone) {
          setShowPhonePopup(true);
          setLoading(false);
          return;
        }

        await sendOtp(email, phone);
      } catch (err) {
        console.error("Login error:", err);
        setError(err.message);
        await doAdminLogout();
        setLoading(false);
      }
    },
    [email, password]
  );

  // Send OTP to both email and phone
  const sendOtp = async (email, phone) => {
    try {
      const response = await fetch("https://sendadminotp-qwfnnmalla-uc.a.run.app", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, phoneNumber: phone }),
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error || "Failed to send OTP.");

      setShowOtpPopup(true);
      setResendDisabled(true);
      setCountdown(30);
    } catch (err) {
      console.error("OTP sending error:", err);
      setError(err.message);
      await doAdminLogout();
    } finally {
      setLoading(false);
    }
  };

  // Handle phone number submission
  const handlePhoneSubmit = useCallback(async () => {
    if (!phoneNumber) {
      setError("Please enter a valid phone number");
      return;
    }

    setLoading(true);
    try {
      await sendOtp(email, phoneNumber);
      setStoredPhoneNumber(phoneNumber);
      setShowPhonePopup(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, [email, phoneNumber]);

  // Verify OTP
  const verifyOtp = useCallback(async () => {
    if (otp.length !== 6) {
      setError("Enter a valid 6-digit OTP.");
      return;
    }

    setOtpLoading(true);
    try {
      const response = await fetch("https://verifyadminotp-qwfnnmalla-uc.a.run.app", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp }),
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error || "Invalid OTP");

      setIsAdmin(true);
      localStorage.setItem("isAdmin", true);
      navigate("/admin-dashboard");
    } catch (err) {
      setError(err.message);
      await doAdminLogout();
    } finally {
      setOtpLoading(false);
    }
  }, [otp, email, setIsAdmin, navigate]);

  // Resend OTP
  const resendOtp = useCallback(async () => {
    setOtpLoading(true);
    setError("");

    try {
      const response = await fetch("https://sendadminotp-qwfnnmalla-uc.a.run.app", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, phoneNumber: storedPhoneNumber || phoneNumber }),
      });

      const data = await response.json();
      if (!data.success) throw new Error("Failed to resend OTP");

      setResendDisabled(true);
      setCountdown(30);
    } catch (err) {
      setError(err.message);
    } finally {
      setOtpLoading(false);
    }
  }, [email, phoneNumber, storedPhoneNumber]);

  // Cancel OTP
  const handleCancelOtp = useCallback(async () => {
    await doAdminLogout();
    setShowOtpPopup(false);
    setShowPhonePopup(false);
    setEmail("");
    setPassword("");
    setPhoneNumber("");
    setStoredPhoneNumber(null);
    setError("");
  }, []);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-800 p-4">
      <div className="w-full max-w-md p-6 bg-white shadow-md rounded-lg">
        <h2 className="text-2xl md:text-3xl font-semibold text-center text-gray-800 mb-6">
          Admin Login
        </h2>
        {error && <p className="text-red-500 text-center mb-4">{error}</p>}

        {!showOtpPopup && !showPhonePopup ? (
          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label className="block text-gray-600 mb-2">Email</label>
              <input
                type="email"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div className="mb-6">
              <label className="block text-gray-600 mb-2">Password</label>
              <input
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <button
              type="submit"
              className="w-full bg-blue-500 hover:bg-blue-600 text-white p-3 rounded-md transition-colors"
              disabled={loading}
            >
              {loading ? <ClipLoader size={20} color="#fff" /> : "Login"}
            </button>
          </form>
        ) : showPhonePopup ? (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
            <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-sm transform transition-all duration-300 animate-fade-in">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Phone Verification</h2>
                <p className="text-gray-500">Please enter your phone number for verification</p>
              </div>

              {error && (
                <div className="bg-red-50 p-3 rounded-lg mb-4">
                  <p className="text-red-600 text-sm text-center">{error}</p>
                </div>
              )}

              <div className="mb-6">
                <input
                  type="tel"
                  placeholder="Enter phone number (e.g., +1234567890)"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                  autoFocus
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handlePhoneSubmit}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white p-3 rounded-xl font-semibold shadow-lg hover:shadow-blue-200 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
                  disabled={loading || !phoneNumber}
                >
                  {loading ? <ClipLoader size={20} color="#fff" /> : "Submit"}
                </button>

                <button
                  onClick={handleCancelOtp}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 p-3 rounded-xl font-semibold transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
            <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-sm transform transition-all duration-300 animate-fade-in">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Enter OTP</h2>
                <p className="text-gray-500">We've sent a 6-digit code to:</p>
                <div className="mt-1 text-sm">
                  <p className="font-medium text-gray-700">+9195xxxxxx82</p>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 p-3 rounded-lg mb-4">
                  <p className="text-red-600 text-sm text-center">{error}</p>
                </div>
              )}

              <div className="mb-6">
                <input
                  type="number"
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.slice(0, 6))}
                  className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all outline-none text-center text-lg font-semibold tracking-widest"
                  autoFocus
                />
              </div>

              <button
                onClick={verifyOtp}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white p-3 rounded-xl font-semibold shadow-lg hover:shadow-green-200 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
                disabled={otpLoading || otp.length !== 6}
              >
                {otpLoading ? <ClipLoader size={20} color="#fff" /> : "Verify OTP"}
              </button>

              <div className="flex justify-between items-center mt-4">
                <button
                  onClick={resendOtp}
                  className="text-blue-500 hover:text-blue-600 font-medium disabled:text-gray-400 disabled:cursor-not-allowed transition-all"
                  disabled={resendDisabled || otpLoading}
                >
                  {resendDisabled ? (
                    <span className="flex items-center gap-1">
                      <span>Resend in</span>
                      <span className="font-semibold">{countdown}s</span>
                    </span>
                  ) : (
                    "Resend OTP"
                  )}
                </button>

                <button
                  onClick={handleCancelOtp}
                  className="text-red-500 hover:text-red-600 font-medium transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLoginPage;
